import Cookies from 'js-cookie';

// Cookie consent types
export const COOKIE_TYPES = {
  NECESSARY: 'necessary',
  ANALYTICS: 'analytics',
  MARKETING: 'marketing',
  FUNCTIONAL: 'functional'
};

// Cookie names
export const COOKIE_NAMES = {
  CONSENT: 'terrapin_cookie_consent',
  ANALYTICS_CONSENT: 'terrapin_analytics_consent',
  MARKETING_CONSENT: 'terrapin_marketing_consent',
  FUNCTIONAL_CONSENT: 'terrapin_functional_consent',
  CONSENT_DATE: 'terrapin_consent_date'
};

// Google Analytics configuration
export const GA_MEASUREMENT_ID = 'G-XXXXXXXXXX'; // Replace with your actual GA4 Measurement ID

class CookieManager {
  constructor() {
    this.consentGiven = this.hasConsent();
    this.preferences = this.getPreferences();
  }

  // Check if user has given consent
  hasConsent() {
    return Cookies.get(COOKIE_NAMES.CONSENT) === 'true';
  }

  // Get user cookie preferences
  getPreferences() {
    return {
      [COOKIE_TYPES.NECESSARY]: true, // Always true
      [COOKIE_TYPES.ANALYTICS]: Cookies.get(COOKIE_NAMES.ANALYTICS_CONSENT) === 'true',
      [COOKIE_TYPES.MARKETING]: Cookies.get(COOKIE_NAMES.MARKETING_CONSENT) === 'true',
      [COOKIE_TYPES.FUNCTIONAL]: Cookies.get(COOKIE_NAMES.FUNCTIONAL_CONSENT) === 'true'
    };
  }

  // Set consent for all cookie types
  setConsent(preferences = {}) {
    const defaultPreferences = {
      [COOKIE_TYPES.NECESSARY]: true,
      [COOKIE_TYPES.ANALYTICS]: false,
      [COOKIE_TYPES.MARKETING]: false,
      [COOKIE_TYPES.FUNCTIONAL]: false,
      ...preferences
    };

    // Set main consent cookie
    Cookies.set(COOKIE_NAMES.CONSENT, 'true', { expires: 365, sameSite: 'Lax' });
    Cookies.set(COOKIE_NAMES.CONSENT_DATE, new Date().toISOString(), { expires: 365, sameSite: 'Lax' });

    // Set individual consent cookies
    Object.entries(defaultPreferences).forEach(([type, consent]) => {
      if (type === COOKIE_TYPES.NECESSARY) return; // Skip necessary cookies
      
      const cookieName = this.getCookieNameForType(type);
      if (cookieName) {
        Cookies.set(cookieName, consent ? 'true' : 'false', { expires: 365, sameSite: 'Lax' });
      }
    });

    this.consentGiven = true;
    this.preferences = defaultPreferences;

    // Initialize services based on consent
    this.initializeServices();

    // Trigger consent change event
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { 
      detail: { preferences: defaultPreferences } 
    }));
  }

  // Get cookie name for type
  getCookieNameForType(type) {
    switch (type) {
      case COOKIE_TYPES.ANALYTICS:
        return COOKIE_NAMES.ANALYTICS_CONSENT;
      case COOKIE_TYPES.MARKETING:
        return COOKIE_NAMES.MARKETING_CONSENT;
      case COOKIE_TYPES.FUNCTIONAL:
        return COOKIE_NAMES.FUNCTIONAL_CONSENT;
      default:
        return null;
    }
  }

  // Initialize services based on consent
  initializeServices() {
    if (this.preferences[COOKIE_TYPES.ANALYTICS]) {
      this.initializeGoogleAnalytics();
    }

    if (this.preferences[COOKIE_TYPES.FUNCTIONAL]) {
      this.initializeFunctionalCookies();
    }

    if (this.preferences[COOKIE_TYPES.MARKETING]) {
      this.initializeMarketingCookies();
    }
  }

  // Initialize Google Analytics
  initializeGoogleAnalytics() {
    if (typeof window === 'undefined' || !GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === 'G-XXXXXXXXXX') {
      console.warn('Google Analytics Measurement ID not configured');
      return;
    }

    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID, {
      cookie_flags: 'SameSite=Lax;Secure',
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false
    });

    console.log('Google Analytics initialized');
  }

  // Initialize functional cookies
  initializeFunctionalCookies() {
    console.log('Functional cookies enabled');
    // Add your functional cookie logic here
    // e.g., user preferences, language settings, etc.
  }

  // Initialize marketing cookies
  initializeMarketingCookies() {
    console.log('Marketing cookies enabled');
    // Add your marketing cookie logic here
    // e.g., social media pixels, advertising networks, etc.
  }

  // Track custom event
  trackEvent(eventName, parameters = {}) {
    if (this.preferences[COOKIE_TYPES.ANALYTICS] && window.gtag) {
      window.gtag('event', eventName, parameters);
    }
  }

  // Track page view
  trackPageView(path) {
    if (this.preferences[COOKIE_TYPES.ANALYTICS] && window.gtag) {
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_path: path
      });
    }
  }

  // Revoke consent and clear cookies
  revokeConsent() {
    // Remove consent cookies
    Object.values(COOKIE_NAMES).forEach(cookieName => {
      Cookies.remove(cookieName);
    });

    // Clear Google Analytics cookies
    const gaCookies = document.cookie.split(';').filter(cookie => 
      cookie.trim().startsWith('_ga') || 
      cookie.trim().startsWith('_gid') ||
      cookie.trim().startsWith('_gat')
    );

    gaCookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      Cookies.remove(cookieName);
      Cookies.remove(cookieName, { domain: '.terrapin.tech' });
      Cookies.remove(cookieName, { domain: window.location.hostname });
    });

    this.consentGiven = false;
    this.preferences = {
      [COOKIE_TYPES.NECESSARY]: true,
      [COOKIE_TYPES.ANALYTICS]: false,
      [COOKIE_TYPES.MARKETING]: false,
      [COOKIE_TYPES.FUNCTIONAL]: false
    };

    // Trigger consent change event
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { 
      detail: { preferences: this.preferences } 
    }));

    // Reload page to clear any remaining tracking
    window.location.reload();
  }

  // Update specific consent type
  updateConsent(type, consent) {
    if (type === COOKIE_TYPES.NECESSARY) return; // Can't change necessary cookies

    const cookieName = this.getCookieNameForType(type);
    if (cookieName) {
      Cookies.set(cookieName, consent ? 'true' : 'false', { expires: 365, sameSite: 'Lax' });
      this.preferences[type] = consent;

      if (consent) {
        if (type === COOKIE_TYPES.ANALYTICS) {
          this.initializeGoogleAnalytics();
        } else if (type === COOKIE_TYPES.FUNCTIONAL) {
          this.initializeFunctionalCookies();
        } else if (type === COOKIE_TYPES.MARKETING) {
          this.initializeMarketingCookies();
        }
      }

      // Trigger consent change event
      window.dispatchEvent(new CustomEvent('cookieConsentChanged', { 
        detail: { preferences: this.preferences } 
      }));
    }
  }

  // Get consent date
  getConsentDate() {
    const dateString = Cookies.get(COOKIE_NAMES.CONSENT_DATE);
    return dateString ? new Date(dateString) : null;
  }

  // Check if consent is expired (older than 1 year)
  isConsentExpired() {
    const consentDate = this.getConsentDate();
    if (!consentDate) return true;

    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    
    return consentDate < oneYearAgo;
  }
}

// Create singleton instance
export const cookieManager = new CookieManager();

// Helper functions for components
export const hasConsentForType = (type) => {
  return cookieManager.preferences[type] || false;
};

export const setConsentForType = (type, consent) => {
  cookieManager.updateConsent(type, consent);
};

export const trackAnalyticsEvent = (eventName, parameters) => {
  cookieManager.trackEvent(eventName, parameters);
};

export const trackPageView = (path) => {
  cookieManager.trackPageView(path);
};

export default cookieManager; 